/* Phase 5.9.2 — Account Security page (staff-accessible).
   Reuses the SecurityPanel from the customer ProfilePage so we have a single
   source of truth for the Change Password + Change Email UI. Dispatchers,
   drivers, and super-admins land here from their dashboard's SECURITY link.
*/
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { BackButton } from '../components/BackButton';
import { SecurityPanel } from './customer/ProfilePage';
import { ModernSecurityPanel } from './customer/ProfileModernPanels';
import { useTheme } from '../context/ThemeContext';
import { ShieldCheck, User } from 'lucide-react';

const ROLE_HOME = {
  customer: '/customer',
  driver: '/driver',
  dispatcher: '/dispatch',
  super_admin: '/dispatch',
};

export function AccountSecurityPage() {
  const { user } = useAuth();
  const homePath = ROLE_HOME[user?.role] || '/';
  const { isModern } = useTheme();

  if (isModern) {
    return (
      <div className="am-modern-scope min-h-screen" data-testid="account-security-page">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-5">
          <div className="flex items-center gap-3">
            <BackButton to={homePath} testid="security-back-link" />
            <div>
              <p className="am-eyebrow">Your account</p>
              <h1 className="text-3xl font-semibold tracking-tight">Account security</h1>
            </div>
          </div>
          <div className="am-card-lg p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--am-emerald-50)' }}>
              <ShieldCheck className="w-6 h-6" style={{ color: 'var(--am-emerald-600)' }} />
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate" style={{ color: 'var(--am-ink-1)' }} data-testid="security-user-name">{user?.name || user?.email}</p>
              <p className="text-sm truncate" style={{ color: 'var(--am-ink-3)' }} data-testid="security-user-email">{user?.email}</p>
            </div>
          </div>
          <p className="text-sm" style={{ color: 'var(--am-ink-3)' }}>
            Both changes ask for your current password. Updating your password signs you out on other devices.
          </p>
          <ModernSecurityPanel />
          <p className="text-sm flex flex-wrap items-center gap-x-2 gap-y-1" style={{ color: 'var(--am-ink-3)' }}>
            <User className="w-4 h-4" />Need to update your name or contact info?
            <Link to="/customer/profile" className="font-medium hover:underline" style={{ color: 'var(--am-emerald-700)' }}>Edit your profile</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050003]" data-testid="account-security-page">
      <Navbar />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-8">
          <BackButton to={homePath} testid="security-back-link" />
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold neon-text">
            {'>'} ACCOUNT_SECURITY
          </h1>
        </div>

        <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
          <div className="flex items-center gap-4 pb-6 border-b border-[#2A0F3D] mb-6">
            <div className="w-16 h-16 bg-[#2A0F3D] flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-[#39FF14]" />
            </div>
            <div>
              <h2 className="text-[#39FF14] font-mono font-bold text-lg" data-testid="security-user-name">
                {user?.name || user?.email}
              </h2>
              <p className="text-[#4B6B52] font-mono text-sm" data-testid="security-user-email">
                {user?.email}
              </p>
              <p className="text-[#6B8E73] font-mono text-xs uppercase mt-1">
                {user?.role} ACCOUNT{user?.region ? ` · ${user.region.toUpperCase()}` : ''}
              </p>
            </div>
          </div>

          <p className="text-[#A3C7A8] font-mono text-xs leading-relaxed mb-4">
            // Change your password or login email. Both flows require your current password
            for re-authentication. Updating your password signs out all other devices.
          </p>

          {/* The SAME panel customers use — just hosted on a role-agnostic page */}
          <SecurityPanel />
        </div>

        <div className="mt-6 flex items-center gap-2 text-[#4B6B52] font-mono text-xs">
          <User className="w-3 h-3" />
          <span>// Need to update your name or contact info?{' '}
            {user?.role === 'customer' ? (
              <Link to="/customer/profile" className="text-[#39FF14] hover:underline">Edit your profile →</Link>
            ) : user?.role === 'driver' ? (
              <Link to="/driver/profile" className="text-[#39FF14] hover:underline">Edit your profile →</Link>
            ) : (
              <span className="text-[#6B8E73]">Reach out to support to update other account fields.</span>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
